import type { IChannel } from "containers/Channels/types";
import type { TFunction } from "i18next";
import type { CalculationBaseColumn, ICalculationBaseColumnLabel } from "store/calculationBaseColumnLabelsStore";
import { ChannelCalculationMethod, ChannelType } from "store/types";

type ChannelBaseSource = Pick<IChannel, "type"> & Partial<Pick<IChannel, "calculationMethod">>;

/**
 * Contract base imposed by the calculation method, mirroring the legacy channel configuration:
 * forfait channels were amount-based, subscription channels are always quantity-based.
 */
const getLockedCalculationBaseColumn = (channel: ChannelBaseSource): CalculationBaseColumn | null => {
    if (channel.calculationMethod === ChannelCalculationMethod.PACKAGE) return "turnover";
    if (channel.calculationMethod === ChannelCalculationMethod.SUBSCRIPTION) return "quantity";
    return null;
};

export const getDefaultCalculationBaseColumn = (channel: ChannelBaseSource): CalculationBaseColumn =>
    getLockedCalculationBaseColumn(channel) ?? (channel.type === ChannelType.AMOUNT_BASED ? "turnover" : "quantity");

export const resolveCalculationBaseColumn = (
    channel: ChannelBaseSource,
    storedValue?: CalculationBaseColumn | null,
): CalculationBaseColumn =>
    getLockedCalculationBaseColumn(channel) ?? storedValue ?? getDefaultCalculationBaseColumn(channel);

export const getCalculationBaseColumnOptions = (
    labels: ICalculationBaseColumnLabel[],
    t: TFunction,
): { value: CalculationBaseColumn; label: string }[] => {
    const labelByColumn = Object.fromEntries(
        labels.map((label) => [label.calculationBaseColumn, label.label]),
    ) as Partial<Record<CalculationBaseColumn, string>>;

    const options: { value: CalculationBaseColumn; label: string }[] = [
        {
            value: "quantity",
            label: labelByColumn.quantity ?? t("CalculationBaseColumnLabels.Columns.quantity"),
        },
        {
            value: "turnover",
            label: labelByColumn.turnover ?? t("CalculationBaseColumnLabels.Columns.turnover"),
        },
    ];

    if (labelByColumn.turnover_1) {
        options.push({ value: "turnover_1", label: labelByColumn.turnover_1 });
    }

    if (labelByColumn.turnover_2) {
        options.push({ value: "turnover_2", label: labelByColumn.turnover_2 });
    }

    return options;
};
